import { Check } from "lucide-react";

/**
 * Gemeinsame Formularbausteine für Kontakt und Warteliste, damit beide
 * Formulare gleich aussehen und später denselben Versand nutzen können.
 */
export const FIELD =
  "w-full rounded-xl border border-white/12 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-brand-blue/60 focus:bg-white/[0.06]";
export const LABEL =
  "block text-xs uppercase tracking-[0.15em] font-bold text-white/45 mb-2";

/** Auswahl als Pillen. `options`: { value, label, icon }. */
export const ChipGroup = ({ label, options, value, onChange }) => (
  <div>
    <span className={LABEL}>{label}</span>
    <div className="flex flex-wrap gap-2.5">
      {options.map((o) => {
        const Icon = o.icon;
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            aria-pressed={active}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-300 ${
              active
                ? "border-white/25 bg-white/[0.10] text-white"
                : "border-white/12 bg-white/[0.03] text-white/60 hover:text-white hover:border-white/25"
            }`}
          >
            <Icon className="w-4 h-4" />
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

/**
 * Verstecktes Feld gegen Bots (siehe useSpamGuard in src/lib/forms.js). Für
 * Menschen unsichtbar und nicht per Tab erreichbar; Bots füllen es aus.
 */
export const Honeypot = ({ id, value, onChange }) => (
  <div aria-hidden="true" className="absolute -left-[9999px] top-0 w-px h-px overflow-hidden">
    <label htmlFor={id}>Website</label>
    <input
      id={id}
      name="website"
      type="text"
      tabIndex={-1}
      autoComplete="off"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

/** Fehlermeldung unter einem Formular. */
export const FormError = ({ children }) =>
  children ? (
    <p
      role="alert"
      className="rounded-xl border border-brand-orange/40 px-4 py-3 text-sm leading-relaxed text-white/75"
    >
      {children}
    </p>
  ) : null;

/**
 * Häkchen mit Text daneben. Das Eingabefeld bleibt ein echtes Kontrollkästchen
 * (nur umgestaltet), damit `required` und die Tastatur wie gewohnt greifen.
 */
export const Checkbox = ({ id, checked, onChange, required, children }) => (
  <label htmlFor={id} className="flex items-start gap-3.5 cursor-pointer">
    <span className="relative flex mt-0.5 shrink-0">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        required={required}
        className="peer appearance-none w-5 h-5 rounded-[5px] border border-white/25 bg-white/[0.03] cursor-pointer transition-colors duration-200 hover:border-white/45 checked:bg-brand-orange checked:border-brand-orange outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/60 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900"
      />
      <Check
        aria-hidden="true"
        strokeWidth={3}
        className="pointer-events-none absolute inset-0 m-auto w-3.5 h-3.5 text-navy-950 opacity-0 peer-checked:opacity-100 transition-opacity duration-200"
      />
    </span>
    <span className="text-sm leading-relaxed text-white/60">{children}</span>
  </label>
);
